import type { TractorWithAxlesResponse } from '@contracts/tractor/TractorWithAxlesResponse';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import { Button } from '@src/common/components/button/Button';
import { IconButton } from '@src/common/components/IconButton';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { NullableFormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  TractorIdField,
  tractorInformationFields,
  TractorUnitNumberField,
} from '@src/permitField/TractorInformationFields';
import { TractorInformationForm } from '@src/tractor/TractorInformationForm';
import { newTractorKey, TractorSelector } from '@src/tractor/TractorSelector';
import { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TractorInformation = NullableFormEntries<string[]>;

type Props = {
  clientId: string;
  onTractorSelect: (
    selectedTractor: TractorWithAxlesResponse | undefined,
  ) => void;
  fields: string[];
  onAddTractor: () => void;
  onEditTractor: () => void;
} & CollapseListener;

export const TractorInformationSection = ({
  clientId,
  onTractorSelect,
  onAddTractor,
  onEditTractor,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const {
    control,
    getValues,
    watch,
    formState: { disabled: formDisabled },
  } = useFormContext<TractorInformation>();
  const selectedTractorId = watch(TractorIdField.fieldName);
  const { isFieldDisabled } = useDisabledFields();

  const {
    current: [unitNumber, id],
  } = useRef(
    getValues([TractorUnitNumberField.fieldName, TractorIdField.fieldName]),
  );

  const previousTractorValues = {
    unitNumber,
    id,
  };

  const { sectionInError } = useSectionError(
    Object.keys(tractorInformationFields),
  );

  const isReadonly = !!(
    selectedTractorId && selectedTractorId !== newTractorKey
  );

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('tractorInformation')}
      id="tractor-information"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']} alignItems="center">
          <Controller
            control={control}
            name={TractorIdField.fieldName}
            render={({ field, fieldState: { error } }) => (
              <TractorSelector
                disabled={
                  formDisabled || isFieldDisabled(TractorIdField.fieldName)
                }
                clientId={clientId}
                error={error}
                defaultValue={
                  previousTractorValues.id
                    ? {
                        label:
                          previousTractorValues.unitNumber?.toString() ??
                          newTractorKey,
                        value: previousTractorValues.id.toString(),
                      }
                    : undefined
                }
                onTractorSelect={onTractorSelect}
                {...field}
              />
            )}
          />
          {selectedTractorId && selectedTractorId !== newTractorKey && (
            <ConditionalField
              fields={props.fields}
              conditions={TractorIdField.fieldName}
            >
              <IconButton
                onClick={onEditTractor}
                title={t('editTractor')}
                disabled={isFieldDisabled(TractorIdField.fieldName)}
              >
                <Edit />
              </IconButton>
            </ConditionalField>
          )}
        </Stack>

        {!formDisabled && (
          <ConditionalField
            fields={props.fields}
            conditions={TractorIdField.fieldName}
          >
            <Button
              onClick={onAddTractor}
              endIcon={<Add />}
              disabled={isFieldDisabled(TractorIdField.fieldName)}
            >
              {t('addTractor')}
            </Button>
          </ConditionalField>
        )}
        <TractorInformationForm
          readonly={isReadonly}
          control={control}
          fields={props.fields}
        />
      </Stack>
    </EditOrderCollapsible>
  );
};
